import React from "react";
import { cn } from "@ndd/ui";
import type { MetricEnvelope, MetricName } from "../../../../../shared/metrics";
import {
  periodColumns,
  sectionHeaderLine,
  type FunctionalFormLink,
  type MetricSection,
} from "../../../lib/metrics-display";
import { PanelHead } from "./panel-head";
import { PeriodTableRow } from "./period-table-row";

/**
 * The narrowest a period table gets before its card scrolls sideways. Header
 * words start running into the next column below about 680px; 760px keeps
 * readable gaps between values and columns (NO-676).
 */
const PERIOD_TABLE_MIN_WIDTH = "min-w-[760px]";

function PeriodTableHead({ labels }: { labels: string[] }) {
  return (
    <thead>
      <tr className="text-caption leading-4 uppercase tracking-[0.44px] text-muted-foreground">
        <th scope="col" className="px-[10px] py-3 text-left font-semibold">
          Metric
        </th>
        {labels.map((label, index) => (
          <th
            key={label}
            scope="col"
            className={cn(
              "w-[9%] px-[10px] py-3 text-right font-semibold",
              index === 0 && "bg-muted text-foreground"
            )}
          >
            {label}
          </th>
        ))}
        <th scope="col" className="w-[22%] px-[10px] py-3 text-left font-semibold">
          Against expectations
        </th>
      </tr>
    </thead>
  );
}

export function PeriodTable({
  section,
  metrics,
  anchor,
  startDate,
  functionalFormLink,
}: {
  section: MetricSection;
  metrics: Record<MetricName, MetricEnvelope>;
  anchor: string;
  startDate: string | undefined;
  functionalFormLink?: FunctionalFormLink;
}) {
  const [firstMetric] = section.metrics;
  const labels = firstMetric
    ? periodColumns(metrics[firstMetric]).map((column) => column.label)
    : [];
  const envelopes = section.metrics.map((name) => metrics[name]);

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <PanelHead title={section.title}>
        <p className="ml-auto text-body text-muted-foreground">
          {sectionHeaderLine(section, envelopes)}
        </p>
      </PanelHead>
      {/* Below the table's minimum width the card scrolls sideways, never the page
          (NO-676). Focusable so keyboard users can scroll it too. */}
      <div
        role="region"
        aria-label={`${section.title} table`}
        tabIndex={0}
        className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <table
          className={cn("w-full table-fixed border-collapse", PERIOD_TABLE_MIN_WIDTH)}
        >
          <PeriodTableHead labels={labels} />
          <tbody>
            {section.metrics.map((name) => (
              <PeriodTableRow
                key={name}
                name={name}
                envelope={metrics[name]}
                cadence={section.cadence}
                anchor={anchor}
                startDate={startDate}
                functionalFormLink={functionalFormLink}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
